import { useCallback, useEffect, useRef, useState } from 'react';
import type { SpeedTier } from './types';

// ---- Helpers ----
export const timeout = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

// ---- Defaults ----
export interface SlotMachineEngineOptions {
  active?: number;
  delay?: number;
  /** 'up' or 'down' — direction items appear to scroll. */
  direction?: 'up' | 'down';
  /** Custom randomize function. (active, total) => nextIndex */
  randomize?: (active: number, total: number) => number;
  /** Enable ease-in/out phases. Default: true. */
  easing?: boolean;
  /** Duration in ms of each easing phase. Default: 600. */
  easeDuration?: number;
}

function defaultRandomize(_active: number, max: number): number {
  return Math.floor(Math.random() * max);
}

const DEFAULT_DELAY = 200;

export interface SlotMachineEngineState {
  /** Current visible item index. */
  active: number;
  /** Is the machine currently spinning? */
  running: boolean;
  /** Is the machine decelerating to a stop? */
  stopping: boolean;
  /** Current speed tier (for blur / visual effects). */
  speedTier: SpeedTier | 'stop';
  /** Callbacks the engine invokes — set by the consumer. */
  callbacks: React.MutableRefObject<SlotMachineCallbacks>;
}

export interface SlotMachineCallbacks {
  onShuffle?: (nextIndex: number) => void;
  onStop?: (winner: { index: number }) => void;
}

export interface SlotMachineEngine extends SlotMachineEngineState {
  shuffle: (spins?: number) => void;
  stop: (spins?: number) => void;
  next: () => void;
  prev: () => void;
}

/**
 * Core slot-machine engine as a React hook.
 *
 * Manages index state, spin-cycle scheduling, speed tiers, and stop logic.
 * The consumer (SlotMachine component) reads `active`, `running`, `stopping`,
 * and `speedTier` to drive CSS animations and blur effects.
 */
export function useSlotMachineEngine(
  itemCount: number,
  options?: SlotMachineEngineOptions,
): SlotMachineEngine {
  // ---- Mutable options ref (avoids stale closures) ----
  const optsRef = useRef<Required<SlotMachineEngineOptions>>({
    active: 0,
    delay: DEFAULT_DELAY,
    direction: 'up',
    randomize: defaultRandomize,
    easing: true,
    easeDuration: 600,
  });

  useEffect(() => {
    optsRef.current = {
      active: options?.active ?? 0,
      delay: options?.delay ?? DEFAULT_DELAY,
      direction: options?.direction ?? 'up',
      randomize: options?.randomize ?? defaultRandomize,
      easing: options?.easing ?? true,
      easeDuration: options?.easeDuration ?? 600,
    };
  }, [options?.active, options?.delay, options?.direction, options?.randomize, options?.easing, options?.easeDuration]);

  // ---- State ----
  const [active, setActive] = useState<number>(() => {
    const a = options?.active ?? 0;
    return a < 0 || a >= itemCount || itemCount === 0 ? 0 : a;
  });

  const activeRef = useRef(active);
  activeRef.current = active;

  const [running, setRunning] = useState(false);
  const runningRef = useRef(false);

  const [stopping, setStopping] = useState(false);
  const stoppingRef = useRef(false);

  const [speedTier, setSpeedTier] = useState<SpeedTier | 'stop'>('stop');

  // Internal mutable state
  const nextActiveRef = useRef<number | null>(null);
  /** Pre-determined winner — set once in shuffle, never overwritten by ticks. */
  const winnerRef = useRef<number | null>(null);
  const remainingRef = useRef(0);
  const abortRef = useRef(false);

  // Callbacks ref
  const callbacks = useRef<SlotMachineCallbacks>({});

  // ---- Index helpers ----
  const prevIdx = useCallback(
    (idx: number) => (idx <= 0 ? itemCount - 1 : idx - 1),
    [itemCount],
  );
  const nextIdx = useCallback(
    (idx: number) => (idx + 1 < itemCount ? idx + 1 : 0),
    [itemCount],
  );

  // ---- Speed calculation with ease-in / ease-out phases ----
  const calcSpeed = useCallback(
    (
      remaining: number,
      elapsed: number,
      isStopping: boolean,
    ): { delay: number; tier: SpeedTier; timing: string } => {
      const baseDelay = optsRef.current.delay;
      const useEasing = optsRef.current.easing;
      const easeMs = optsRef.current.easeDuration;
      // Convert ease duration to tick counts (approximate)
      const easeTicks = Math.max(2, Math.round(easeMs / baseDelay));

      let d = baseDelay;
      let tier: SpeedTier = 'fast';
      let timing = 'linear';

      if (!useEasing) {
        // Constant speed — no acceleration/deceleration
        d = isStopping ? baseDelay * 2 : baseDelay / 1.5;
        tier = isStopping ? 'turtle' : 'fast';
        timing = 'linear';
      } else if (isStopping) {
        // Ease-out: decelerate over easeTicks
        const decelRemaining = Math.min(remaining, easeTicks);
        const progress = Math.max(0, Math.min(1, (easeTicks - decelRemaining) / (easeTicks - 1)));
        const multiplier = 1 + progress * 4; // 1x → 5x base delay
        d = baseDelay * multiplier;
        tier = decelRemaining <= 2 ? 'turtle' : decelRemaining <= easeTicks / 2 ? 'slow' : 'normal';
        timing = 'ease-out';
      } else if (elapsed < easeTicks) {
        // Ease-in: accelerate over first easeTicks (delay 2.5x → 1x)
        const progress = elapsed / easeTicks;
        const multiplier = 2.5 - progress * 1.5;
        d = baseDelay * multiplier;
        tier = elapsed < easeTicks / 2 ? 'slow' : 'normal';
        timing = 'ease-out';
      } else {
        // Constant-speed phase
        d = baseDelay / 1.5;
        tier = 'fast';
        timing = 'linear';
      }

      return { delay: d, tier, timing };
    },
    [],
  );

  // ---- One tick of spinning ----
  const tick = useCallback(
    async (_remaining: number, elapsed: number): Promise<void> => {
      if (abortRef.current) return;

      // Always read fresh refs — doStop may mutate them during await
      const remaining = remainingRef.current;
      const isStopping = stoppingRef.current;

      const { delay, tier } = calcSpeed(remaining, elapsed, isStopping);
      setSpeedTier(tier);

      // Determine the index to show for this tick
      const isFinalTick = isStopping && remaining <= 1;
      const next = isFinalTick
        ? winnerRef.current! // final tick → use pre-determined winner (never clobbered)
        : optsRef.current.randomize(activeRef.current, itemCount);

      if (!isFinalTick) {
        nextActiveRef.current = next;
      }
      callbacks.current.onShuffle?.(next);

      // Update active → consumer animates via CSS transition
      activeRef.current = next;
      setActive(next);

      await timeout(delay);
      if (abortRef.current) return;

      // Decrement — using the ref value (may have been capped by doStop during await)
      const newRemaining = remainingRef.current - 1;
      remainingRef.current = newRemaining;

      // Re-read stopping state (doStop may have fired during await)
      if (stoppingRef.current) {
        // In stopping phase: decelerate through remaining ticks, then finish
        if (newRemaining >= 1) {
          await tick(newRemaining, elapsed + 1);
        } else {
          // All deceleration ticks exhausted → finalize stop
          setSpeedTier('stop');
          stoppingRef.current = false;
          runningRef.current = false;
          setStopping(false);
          setRunning(false);
          nextActiveRef.current = null;
          winnerRef.current = null;
          callbacks.current.onStop?.({ index: next });
        }
      } else if (runningRef.current) {
        if (newRemaining >= 1) {
          await tick(newRemaining, elapsed + 1);
        } else {
          // All spins exhausted → execute stop sequence
          await doStop(0);
        }
      }
    },
    [itemCount, calcSpeed],
  );

  // ---- Stop sequence (decelerate & land on winner) ----
  const doStop = useCallback(
    async (extraSpins: number) => {
      // If stop() is called with extra spins while still running, just update the counter
      if (runningRef.current && !stoppingRef.current && extraSpins > 0) {
        remainingRef.current = extraSpins;
        return;
      }

      if (!runningRef.current || stoppingRef.current) return;

      stoppingRef.current = true;
      setStopping(true);

      // Ensure we have enough ticks for a visible deceleration
      const currentRemaining = remainingRef.current;
      if (currentRemaining > 6 || !isFinite(currentRemaining)) {
        remainingRef.current = 6; // cap at 6 deceleration ticks
      } else if (currentRemaining < 3) {
        remainingRef.current = 3; // pad to at least 3 deceleration ticks
      }
      // else: 3–6 ticks remaining — decelerate naturally through them
    },
    [],
  );

  // ---- Public API ----
  const shuffle = useCallback(
    (spins: number = 5) => {
      if (runningRef.current || stoppingRef.current || itemCount === 0) return;

      abortRef.current = false;
      runningRef.current = true;
      setRunning(true);

      // Pre-determine the winner — store in dedicated ref that ticks never overwrite
      const winner = optsRef.current.randomize(activeRef.current, itemCount);
      winnerRef.current = winner;
      nextActiveRef.current = winner;
      remainingRef.current = spins;

      tick(spins, 0);
    },
    [itemCount, tick],
  );

  const stop = useCallback(
    (spins: number = 0) => {
      if (!runningRef.current || stoppingRef.current) {
        // Allow scheduling extra spins while running
        if (runningRef.current && !stoppingRef.current && spins > 0) {
          remainingRef.current = spins;
        }
        return;
      }
      doStop(spins);
    },
    [doStop],
  );

  const next = useCallback(() => {
    if (runningRef.current || stoppingRef.current || itemCount === 0) return;
    const nxt = nextIdx(activeRef.current);
    nextActiveRef.current = nxt;
    runningRef.current = true;
    stoppingRef.current = true;
    setRunning(true);
    setStopping(true);
    setSpeedTier('turtle');

    const { delay } = calcSpeed(1, 0, true);
    timeout(delay).then(() => {
      activeRef.current = nxt;
      setActive(nxt);
      setSpeedTier('stop');
      stoppingRef.current = false;
      runningRef.current = false;
      setStopping(false);
      setRunning(false);
      nextActiveRef.current = null;
      callbacks.current.onStop?.({ index: nxt });
    });
  }, [itemCount, nextIdx, calcSpeed]);

  const prev = useCallback(() => {
    if (runningRef.current || stoppingRef.current || itemCount === 0) return;
    const prv = prevIdx(activeRef.current);
    nextActiveRef.current = prv;
    runningRef.current = true;
    stoppingRef.current = true;
    setRunning(true);
    setStopping(true);
    setSpeedTier('turtle');

    const { delay } = calcSpeed(1, 0, true);
    timeout(delay).then(() => {
      activeRef.current = prv;
      setActive(prv);
      setSpeedTier('stop');
      stoppingRef.current = false;
      runningRef.current = false;
      setStopping(false);
      setRunning(false);
      nextActiveRef.current = null;
      callbacks.current.onStop?.({ index: prv });
    });
  }, [itemCount, prevIdx, calcSpeed]);

  return {
    active,
    running,
    stopping,
    speedTier,
    callbacks,
    shuffle,
    stop,
    next,
    prev,
  };
}

